import { useEffect, useState } from "react";
import { Field, FieldColSize } from "./Field";
import { AutoComplete, Button, Checkbox, Flex, Form, Input, InputNumber, Popconfirm, Radio, Select, theme } from "antd";
import { generateRandomString } from "../../../shared/utils";
import { DeleteOutlined, RightCircleOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import { useSelect } from "@refinedev/antd";

export interface AutocompleteFieldOption {
    value: string;
    label?: string;
}

const DEFAULT_FIELD_COL_SIZE: FieldColSize = {
    xs: 24,
    sm: 12,
    md: 8,
    lg: 8,
    xl: 8,
};

const normalizeColSize = (colSize?: Partial<FieldColSize>): FieldColSize => {
    return {
        ...DEFAULT_FIELD_COL_SIZE,
        ...(colSize ?? {}),
    };
};

const SEARCH_DEBOUNCE_MS = 500;

const AutocompleteFieldRenderer: React.FC<{ field: AutocompleteField }> = ({ field }) => {
    const { token } = theme.useToken();
    const t = useTranslate();
    const form = Form.useFormInstance();
    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const watchedValue = field.dependsOnField && form ? Form.useWatch(field.dependsOnField, form) : undefined;
    const hasWatchedValue = watchedValue != null && watchedValue !== "";

    const filters =
        field.dependsOnField && field.dependsOnFilterField && hasWatchedValue
            ? [{ field: field.dependsOnFilterField, operator: "eq" as const, value: watchedValue }]
            : [];

    const rawResource = field.optionsResource?.trim() || "";
    const hasValuePlaceholder = rawResource.includes("{value}");
    const effectiveResource = !rawResource
        ? "forms"
        : hasValuePlaceholder
            ? field.dependsOnField && hasWatchedValue
                ? rawResource.replace(/\{value\}/g, String(watchedValue))
                : "forms"
            : rawResource;

    const useEndpoint = !!field.optionsResource;
    const hasDebouncedSearch = debouncedSearchText.trim().length > 0;
    const enabled =
        useEndpoint &&
        (field.dependsOnField ? hasWatchedValue : true) &&
        hasDebouncedSearch;

    const { selectProps, queryResult } = useSelect({
        resource: effectiveResource,
        optionLabel: (item: any) => String(item ?? ""),
        optionValue: (item: any) => String(item ?? ""),
        filters,
        queryOptions: { enabled },
    });

    useEffect(() => {
        const id = window.setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(id);
    }, [searchText]);

    useEffect(() => {
        if (!field.dependsOnField || !form) return;
        form.setFieldValue(field.name, undefined);
        setSearchText("");
        setDebouncedSearchText("");
    }, [field.dependsOnField, field.name, form, watchedValue]);

    const options = useEndpoint
        ? (hasDebouncedSearch ? (selectProps.options ?? []) : [])
        : (field.options ?? []).map((o) => ({
              value: o.value,
              label: o.label ?? o.value,
          }));
    const isSearchDebouncing =
        useEndpoint && searchText.trim().length > 0 && searchText !== debouncedSearchText;
    const loading =
        useEndpoint &&
        searchText.trim().length > 0 &&
        (isSearchDebouncing || (queryResult?.isLoading ?? false));
    const isDependentDisabled = field.dependsOnField && !hasWatchedValue;
    const placeholder = isDependentDisabled
        ? t("forms.selectOptionFirst")
        : t("forms.selectPlaceholder");

    return (
        <div className={field.hidden ? "hidden" : undefined}>
            <Form.Item
                label={
                    <Flex gap={8}>
                        <RightCircleOutlined style={{ color: token.colorPrimary }} />
                        {field.label}
                    </Flex>
                }
                rules={[{ required: !!field.required }]}
                name={field.name}
                initialValue={field.defaultValue}
            >
                <AutoComplete
                    allowClear
                    options={options}
                    placeholder={placeholder}
                    disabled={!!isDependentDisabled}
                    onSearch={setSearchText}
                    filterOption={(input, option) =>
                        (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
                    }
                />
            </Form.Item>
        </div>
    );
};

export class AutocompleteField implements Field {
    id = generateRandomString(10);
    name = "AutocompleteField" + generateRandomString(4);
    description = "An autocomplete dropdown with search";
    label = "Autocomplete";
    required = false;
    defaultValue: string | undefined = undefined;
    hidden = false;
    colSize: FieldColSize = DEFAULT_FIELD_COL_SIZE;
    formTag = "AutocompleteField";
    parent: Field | null = null;
    options: AutocompleteFieldOption[] = [];
    optionsResource?: string;
    optionLabel?: string;
    optionValue?: string;
    dependsOnField?: string;
    dependsOnFilterField?: string;

    constructor(isTemplate = false) {
        if (isTemplate) {
            this.name = "Autocomplete";
        }
    }

    render = () => {
        return <AutocompleteFieldRenderer field={this} />;
    };

    renderEditor = (
        field: Field,
        selectedComponent?: unknown,
        setSelectedComponent?: unknown,
        deleteComponent?: (c: Field) => void
    ) => {
        return (
            <AutocompleteFieldEditor
                field={field}
                deleteComponent={deleteComponent ?? (() => {})}
            />
        );
    };

    renderPropertiesEditor = (field: Field, updateField: (f: Field) => void, context?: { formFieldNames?: { value: string; label: string }[] }) => {
        return (
            <AutocompleteFieldPropsEditor
                inputField={field}
                setInputField={updateField}
                formFieldNames={context?.formFieldNames ?? []}
            />
        );
    };

    fromJSON = (json: any, parent: Field | null = null): Field => AutocompleteField.fromJSONStatic(json, parent);

    static fromJSONStatic = (json: any, parent: Field | null = null): AutocompleteField => {
        const autocomplete = new AutocompleteField();
        autocomplete.id = json.id;
        autocomplete.label = json.label;
        autocomplete.name = json.name;
        autocomplete.description = json.description;
        autocomplete.required = json.required;
        autocomplete.defaultValue = json.defaultValue;
        autocomplete.hidden = json.hidden ?? false;
        autocomplete.colSize = normalizeColSize(json.colSize);
        autocomplete.formTag = json.formTag;
        autocomplete.parent = parent;
        autocomplete.options = Array.isArray(json.options)
            ? json.options.map((o: any) => ({
                  value: o.value ?? "",
                  label: o.label,
              }))
            : [];
        autocomplete.optionsResource = json.optionsResource;
        autocomplete.optionLabel = json.optionLabel;
        autocomplete.optionValue = json.optionValue;
        autocomplete.dependsOnField = json.dependsOnField;
        autocomplete.dependsOnFilterField = json.dependsOnFilterField;
        return autocomplete;
    };

    static fromJSON = AutocompleteField.fromJSONStatic;

    toJSON = () => {
        return {
            id: this.id,
            name: this.name,
            description: this.description,
            label: this.label,
            required: this.required,
            defaultValue: this.defaultValue,
            hidden: this.hidden,
            colSize: this.colSize,
            formTag: this.formTag,
            parent: this.parent?.id,
            options: this.options,
            ...(this.optionsResource != null && { optionsResource: this.optionsResource }),
            ...(this.optionLabel != null && { optionLabel: this.optionLabel }),
            ...(this.optionValue != null && { optionValue: this.optionValue }),
            ...(this.dependsOnField != null && { dependsOnField: this.dependsOnField }),
            ...(this.dependsOnFilterField != null && { dependsOnFilterField: this.dependsOnFilterField }),
        };
    };
}

export const AutocompleteFieldEditor = ({
    field,
    deleteComponent,
}: {
    field: Field;
    deleteComponent: (c: Field) => void;
}) => {
    const t = useTranslate();
    return (
        <Flex justify="space-between">
            <Form.Item
                label={
                    <span>
                        {field.label}
                        {field.hidden && (
                            <span className="ml-2 text-gray-400 text-xs">
                                ({t("forms.fieldHidden")})
                            </span>
                        )}
                    </span>
                }
                name={field.name}
                className="w-full"
                rules={[{ required: field.required }]}
            >
                <span className="text-gray-500">{t("forms.fieldAutocomplete")}</span>
            </Form.Item>
            <Popconfirm
                title="Delete the item"
                description="Are you sure to delete this item?"
                onConfirm={() => deleteComponent(field)}
                okText="Yes"
                cancelText="No"
            >
                <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
        </Flex>
    );
};

export const AutocompleteFieldPropsEditor = ({
    inputField,
    setInputField,
    formFieldNames = [],
}: {
    inputField: Field;
    setInputField: (f: Field) => void;
    formFieldNames?: { value: string; label: string }[];
}) => {
    const t = useTranslate();
    const [required, setRequired] = useState(inputField.required);
    const [hidden, setHidden] = useState(inputField.hidden ?? false);
    const field = inputField as unknown as AutocompleteField;
    const options = field.options ?? [];
    const colSize = normalizeColSize(field.colSize);

    const handleInputChange = (prop: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        (inputField as any)[prop] = e.target.value;
        setInputField(inputField);
    };

    const handleCheckboxChange = (prop: string) => (e: { target: { checked: boolean } }) => {
        const checked = e.target.checked;
        if (prop === "required") setRequired(checked);
        if (prop === "hidden") setHidden(checked);
        (inputField as any)[prop] = checked;
        setInputField(inputField);
    };

    const addOption = () => {
        field.options = [...options, { value: "", label: "" }];
        setInputField(inputField);
    };

    const updateOption = (index: number, key: "value" | "label", value: string) => {
        const next = [...options];
        next[index] = { ...next[index], [key]: value };
        field.options = next;
        setInputField(inputField);
    };

    const removeOption = (index: number) => {
        field.options = options.filter((_, i) => i !== index);
        setInputField(inputField);
    };

    const handleColSizeChange = (key: keyof FieldColSize) => (value: number | null) => {
        const nextValue = Math.min(24, Math.max(1, Number(value ?? DEFAULT_FIELD_COL_SIZE[key])));
        field.colSize = {
            ...normalizeColSize(field.colSize),
            [key]: nextValue,
        };
        setInputField(inputField);
    };

    return (
        <Form layout="vertical">
            <Form.Item label="Name">
                <Input
                    value={inputField.name}
                    onChange={handleInputChange("name")}
                />
            </Form.Item>
            <Form.Item label="Description">
                <Input
                    value={inputField.description}
                    onChange={handleInputChange("description")}
                />
            </Form.Item>
            <Form.Item label="Label">
                <Input
                    value={inputField.label}
                    onChange={handleInputChange("label")}
                />
            </Form.Item>
            <Form.Item label="Required">
                <Checkbox
                    checked={required}
                    onChange={handleCheckboxChange("required")}
                />
            </Form.Item>
            <Form.Item label={t("forms.fieldHidden")}>
                <Checkbox
                    checked={hidden}
                    onChange={handleCheckboxChange("hidden")}
                />
            </Form.Item>
            <Form.Item label="Default Value">
                <Input
                    value={inputField.defaultValue ?? ""}
                    onChange={handleInputChange("defaultValue")}
                />
            </Form.Item>
            <Form.Item label={t("forms.columnSize")}>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeXs")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.xs} onChange={handleColSizeChange("xs")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeSm")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.sm} onChange={handleColSizeChange("sm")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeMd")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.md} onChange={handleColSizeChange("md")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeLg")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.lg} onChange={handleColSizeChange("lg")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{t("forms.columnSizeXl")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.xl} onChange={handleColSizeChange("xl")} />
                    </div>
                </div>
            </Form.Item>
            <Form.Item label={t("forms.optionsSource")}>
                <Radio.Group
                    value={field.optionsResource ? "endpoint" : "static"}
                    onChange={(e) => {
                        if (e.target.value === "static") {
                            field.optionsResource = undefined;
                            field.optionLabel = undefined;
                            field.optionValue = undefined;
                            field.dependsOnField = undefined;
                            field.dependsOnFilterField = undefined;
                        } else {
                            field.optionsResource = field.optionsResource || "accounts";
                        }
                        setInputField(inputField);
                    }}
                    className="w-full"
                >
                    <Radio value="static">{t("forms.optionsSourceStatic")}</Radio>
                    <Radio value="endpoint">{t("forms.optionsSourceEndpoint")}</Radio>
                </Radio.Group>
            </Form.Item>
            {field.optionsResource ? (
                <>
                    <Form.Item label={t("forms.optionsResource")}>
                        <Input
                            value={field.optionsResource ?? ""}
                            onChange={(e) => {
                                field.optionsResource = e.target.value || undefined;
                                setInputField(inputField);
                            }}
                            placeholder={t("forms.optionsResource")}
                        />
                    </Form.Item>
                    <Form.Item label={t("forms.optionLabelField")}>
                        <Input
                            value={field.optionLabel ?? "name"}
                            onChange={(e) => {
                                field.optionLabel = e.target.value || undefined;
                                setInputField(inputField);
                            }}
                            placeholder="name"
                        />
                    </Form.Item>
                    <Form.Item label={t("forms.optionValueField")}>
                        <Input
                            value={field.optionValue ?? "id"}
                            onChange={(e) => {
                                field.optionValue = e.target.value || undefined;
                                setInputField(inputField);
                            }}
                            placeholder="id"
                        />
                    </Form.Item>
                    <Form.Item label={t("forms.dependsOnField")}>
                        <Select
                            className="w-full"
                            placeholder={t("forms.dependsOnFieldPlaceholder")}
                            value={field.dependsOnField || undefined}
                            onChange={(v) => {
                                field.dependsOnField = v ?? undefined;
                                setInputField(inputField);
                            }}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
                            }
                            options={formFieldNames.filter((o) => o.value !== inputField.name).map((o) => ({ value: o.value, label: o.label }))}
                        />
                    </Form.Item>
                    <Form.Item label={t("forms.dependsOnFilterField")}>
                        <Input
                            value={field.dependsOnFilterField ?? ""}
                            onChange={(e) => {
                                field.dependsOnFilterField = e.target.value || undefined;
                                setInputField(inputField);
                            }}
                            placeholder={t("forms.dependsOnFilterFieldPlaceholder")}
                        />
                    </Form.Item>
                </>
            ) : (
                <>
                    <Form.Item label={t("forms.addOption")}>
                        <Button type="dashed" onClick={addOption} className="w-full">
                            {t("forms.addOption")}
                        </Button>
                    </Form.Item>
                    {options.length > 0 && (
                        <Form.Item label={t("forms.options")}>
                            <div className="flex flex-col gap-2">
                                {options.map((opt, index) => (
                                    <Flex key={index} gap={8} align="center">
                                        <Input
                                            placeholder={t("forms.optionValue")}
                                            value={opt.value}
                                            onChange={(e) =>
                                                updateOption(index, "value", e.target.value)
                                            }
                                            className="flex-1"
                                        />
                                        <Input
                                            placeholder={t("forms.optionLabel")}
                                            value={opt.label ?? ""}
                                            onChange={(e) =>
                                                updateOption(index, "label", e.target.value)
                                            }
                                            className="flex-1"
                                        />
                                        <Popconfirm
                                            title="Delete this option?"
                                            onConfirm={() => removeOption(index)}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            />
                                        </Popconfirm>
                                    </Flex>
                                ))}
                            </div>
                        </Form.Item>
                    )}
                </>
            )}
        </Form>
    );
};
